import React, { useState, useEffect } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { collection, getDocs } from "firebase/firestore";
import {
  BsCheckSquareFill,
  BsSquare,
  BsCircleFill,
  BsCircle,
} from "react-icons/bs";

const FilterDropdown = ({
  db,
  onFilterChange,
  switchPage,
  pageButton,
  isYear2026,
  onGoYear2026,
  onGoHome,
  homeOnly,
}) => {
  const [options, setOptions] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({
    division: [],
    year: [],
    school: [],
    finalist: [],
    winner: [],
    award: [],
    elo: ["all"], // Initialize with "all" selected
  });

  const eloOptions = ["all", "1000", "1025", "1050", "1075", "1100"];

  useEffect(() => {
    const fetchOptions = async () => {
      const querySnapshot = await getDocs(collection(db, "videos"));

      const divisionsSet = new Set();
      const yearsSet = new Set();
      const schoolsSet = new Set();
      const awardsSet = new Set();

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.division) divisionsSet.add(data.division);
        if (data.year) yearsSet.add(data.year);
        if (data.school) schoolsSet.add(data.school);
        if (data.award) awardsSet.add(data.award);
      });

      setOptions({
        division: Array.from(divisionsSet),
        year: Array.from(yearsSet).sort((a, b) => b - a),
        school: Array.from(schoolsSet).sort((a, b) => a.localeCompare(b)),
        winner: ["Winner", "Finalist", "Nominee"],
        award: [...Array.from(awardsSet), "Callahan", "Donovan"].filter(
          (value, index, self) => self.indexOf(value) === index
        ),
        elo: eloOptions,
      });
    };
    fetchOptions();
  }, [db]);

  useEffect(() => {
    onFilterChange(selectedOptions);
  }, [selectedOptions, onFilterChange]);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const toggleOption = (category, option) => {
    setSelectedOptions((prevState) => {
      const newSelection = { ...prevState };

      // Special handling for Elo category (radio button behavior)
      if (category === "elo") {
        // If clicking on already selected option, do nothing
        // to maintain the rule that one option must be selected
        if (newSelection[category].includes(option)) {
          return prevState;
        }
        // Otherwise replace current selection with new option
        newSelection[category] = [option];
      } else {
        // Standard multi-select behavior for other categories
        if (newSelection[category].includes(option)) {
          newSelection[category] = newSelection[category].filter(
            (item) => item !== option
          );
        } else {
          newSelection[category] = [...newSelection[category], option];
        }
      }
      return newSelection;
    });
  };

  const toggleAllOptions = (category) => {
    setSelectedOptions((prevState) => {
      const newSelection = { ...prevState };
      newSelection[category] = [];
      return newSelection;
    });
  };

  const renderSelectionIcon = (category, option) => {
    // For Elo category, use circle icons for radio button appearance
    if (category === "elo") {
      return selectedOptions[category].includes(option) ? (
        <BsCircleFill />
      ) : (
        <BsCircle />
      );
    }

    // For other categories, use square icons for checkbox appearance
    return selectedOptions[category].includes(option) ? (
      <BsCheckSquareFill />
    ) : (
      <BsSquare />
    );
  };

  const isAllSelected = (category) => {
    return (
      options[category]?.every(
        (option) => !selectedOptions[category].includes(option)
      ) || selectedOptions[category].length === 0
    );
  };

  return (
    <div className="filter-dropdown">
      <div className="nav-controls">
        <div className="filter-dropdown-button">
          {isYear2026 || homeOnly ? (
            <button onClick={onGoHome} className="filter-button">
              ← Home
            </button>
          ) : (
            <>
              <button onClick={toggleDropdown} className="filter-button">
                Filter {isOpen ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              <button onClick={onGoYear2026} className="year-button">
                2026
              </button>
            </>
          )}
        </div>
        <div>
          <a href="https://buymeacoffee.com/callahanvideos" target="_blank">
            <button className="coffee-button">Buy Me a Coffee ☕</button>
          </a>
          {!isYear2026 && !homeOnly && (
            <button className="page-button" onClick={switchPage}>
              {pageButton}
            </button>
          )}
        </div>
      </div>
      {isOpen && !isYear2026 && !homeOnly && (
        <div className="dropdown-content">
          <div className="filter-categories">
            {["division", "year", "school", "winner", "award", "elo"].map(
              (category) => (
                <div key={category} className="filter-category">
                  <h4>
                    {category === "winner"
                      ? "Winner?"
                      : category.charAt(0).toUpperCase() + category.slice(1)}
                  </h4>
                  {/* For Elo, we don't need an "All" option as it's in the options list */}
                  {category !== "elo" && (
                    <button
                      onClick={() => toggleAllOptions(category)}
                      className="option-button"
                    >
                      {isAllSelected(category) ? (
                        <BsCheckSquareFill />
                      ) : (
                        <BsSquare />
                      )}
                      <span style={{ marginLeft: "5px" }}>All</span>
                    </button>
                  )}
                  {options[category]?.map((option) => (
                    <div key={option} className="option-item">
                      <button
                        onClick={() => toggleOption(category, option)}
                        className="option-button"
                      >
                        {renderSelectionIcon(category, option)}
                        <span style={{ marginLeft: "5px" }}>
                          {option === "all" ? "All" : option}
                          {category === "elo" && option !== "all" ? "+" : null}
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;
